import { useState, useEffect } from "react";
import {
  Box, Button, Stack, Typography, TextField, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton,
} from "@mui/material";
import EditIcon   from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon    from "@mui/icons-material/Add";

// ── IP Presets ────────────────────────────────────────────────────────────────

const IP_PRESETS = [
  { value: "home_net",     label: "Internal network", desc: "All local network addresses" },
  { value: "external_net", label: "External network", desc: "Any address outside the local network" },
  // { value: "dns_servers",  label: "DNS servers",      desc: "Configured DNS servers" },
  // { value: "http_servers", label: "HTTP servers",     desc: "Configured HTTP servers" },
];
const IP_PRESET_MAP = Object.fromEntries(IP_PRESETS.map(p => [p.value, p]));

const IP_TYPE_LABELS   = { any: "Any", single: "Single", list: "List", preset: "Preset" };
const PORT_TYPE_LABELS = { any: "Any", single: "Single", range: "Range", list: "List" };

// ── Validation helpers ────────────────────────────────────────────────────────

const IP_CIDR_RE = /^((25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(25[0-5]|2[0-4]\d|[01]?\d\d?)(\/([12]?\d|3[0-2]))?$/;
const DOMAIN_RE  = /^(\*\.)?([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

function isValidIpEntry(val, forDns) {
  if (!val) return false;
  return IP_CIDR_RE.test(val) || (forDns && DOMAIN_RE.test(val));
}
function isValidPortNum(p) { return /^\d+$/.test(p) && Number(p) >= 1 && Number(p) <= 65535; }

// ── IP utilities (exported) ───────────────────────────────────────────────────

export function displayIpUi(ui) {
  if (!ui || ui.type === "any")  return "any";
  if (ui.type === "preset")      return IP_PRESET_MAP[ui.value]?.label ?? ui.value;
  if (ui.type === "single")      return `${ui.negate ? "! " : ""}${ui.value}`;
  if (ui.type === "list") {
    const parts = (ui.items ?? []).map(i => `${i.negate ? "! " : ""}${i.value}`);
    if (parts.length === 0) return "—";
    if (parts.length <= 2)  return parts.join(", ");
    return `${parts[0]}, ${parts[1]} +${parts.length - 2}`;
  }
  return "—";
}

export function translateIpUi(ui) {
  if (!ui || ui.type === "any") return "ANY";
  const SURICATA = { home_net: "$HOME_NET", external_net: "$EXTERNAL_NET", dns_servers: "$DNS_SERVERS", http_servers: "$HTTP_SERVERS" };
  if (ui.type === "preset") return SURICATA[ui.value] ?? "ANY";
  if (ui.type === "single") return ui.negate ? `!${ui.value}` : ui.value;
  if (ui.type === "list") {
    const parts = (ui.items ?? []).map(i => i.negate ? `!${i.value}` : i.value);
    return parts.length === 1 ? parts[0] : `[${parts.join(", ")}]`;
  }
  return "ANY";
}

export function isIpUiAdvanced(ui) {
  return ui?.type === "list" || ui?.type === "preset";
}

// ── Port utilities (exported) ─────────────────────────────────────────────────

export function displayPortUi(ui) {
  if (!ui || ui.type === "any") return "any";
  if (ui.type === "single")     return `${ui.negate ? "! " : ""}${ui.value}`;
  if (ui.type === "range")      return `${ui.negate ? "! " : ""}${ui.from}:${ui.to}`;
  if (ui.type === "list") {
    const parts = (ui.items ?? []).map(i =>
      i.type === "range"
        ? `${i.negate ? "! " : ""}${i.from}:${i.to}`
        : `${i.negate ? "! " : ""}${i.value}`
    );
    if (parts.length === 0) return "—";
    if (parts.length <= 2)  return parts.join(", ");
    return `${parts[0]}, ${parts[1]} +${parts.length - 2}`;
  }
  return "—";
}

export function translatePortUi(ui) {
  if (!ui || ui.type === "any") return "ANY";
  if (ui.type === "single")     return ui.negate ? `!${ui.value}` : ui.value;
  if (ui.type === "range")      return ui.negate ? `!${ui.from}:${ui.to}` : `${ui.from}:${ui.to}`;
  if (ui.type === "list") {
    const parts = (ui.items ?? []).map(i =>
      i.type === "range"
        ? (i.negate ? `!${i.from}:${i.to}` : `${i.from}:${i.to}`)
        : (i.negate ? `!${i.value}` : i.value)
    );
    return parts.length === 1 ? parts[0] : `[${parts.join(", ")}]`;
  }
  return "ANY";
}

export function parsePortString(portStr) {
  if (!portStr || portStr.toUpperCase() === "ANY") return { type: "any" };
  if (/^\d+$/.test(portStr))      return { type: "single", value: portStr,            negate: false };
  if (/^!\d+$/.test(portStr))     return { type: "single", value: portStr.slice(1),   negate: true  };
  if (/^!\d+:\d+$/.test(portStr)) { const [f, t] = portStr.slice(1).split(":"); return { type: "range", from: f, to: t, negate: true  }; }
  if (/^\d+:\d+$/.test(portStr))  { const [f, t] = portStr.split(":");            return { type: "range", from: f, to: t, negate: false }; }
  if (/^\[[\d,:!\s]+\]$/.test(portStr)) {
    const items = portStr.slice(1, -1).split(",").map(s => s.trim()).map(item => {
      if (/^!\d+:\d+$/.test(item)) { const [f, t] = item.slice(1).split(":"); return { type: "range", from: f, to: t, negate: true  }; }
      if (/^\d+:\d+$/.test(item))  { const [f, t] = item.split(":");          return { type: "range", from: f, to: t, negate: false }; }
      if (/^!\d+$/.test(item))     return { type: "single", value: item.slice(1), negate: true  };
      return                               { type: "single", value: item,          negate: false };
    });
    return { type: "list", items };
  }
  return { type: "single", value: portStr, negate: false };
}

export function isPortUiAdvanced(ui) {
  return ui?.type === "range" || ui?.type === "list";
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const TEAL = "#449095";

const SWITCH_SX = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#c0392b" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#c0392b" },
};

function pillSx(active) {
  return {
    px: 1.5, py: 0.35, borderRadius: 3, fontSize: "0.72rem", fontWeight: 700, textTransform: "none",
    border: `1px solid ${active ? TEAL : "#2a2a2a"}`,
    color: active ? TEAL : "#555",
    backgroundColor: active ? "rgba(69,144,149,0.12)" : "transparent",
    "&:hover": { backgroundColor: "rgba(69,144,149,0.08)", borderColor: TEAL, color: TEAL },
  };
}

function subPillSx(active) {
  return {
    px: 1.25, py: 0.25, borderRadius: 2, fontSize: "0.68rem", fontWeight: 700, textTransform: "none",
    border: `1px solid ${active ? "rgba(69,144,149,0.7)" : "#2a2a2a"}`,
    color: active ? TEAL : "#444",
    backgroundColor: active ? "rgba(69,144,149,0.1)" : "transparent",
    "&:hover": { borderColor: TEAL, color: TEAL },
  };
}

const dialogPaperSx = {
  sx: {
    backgroundColor: "#141414",
    border: "1px solid rgba(69,144,149,0.28)",
    borderRadius: 2,
    boxShadow: "0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(69,144,149,0.08)",
  }
};

const addBtnSx = {
  alignSelf: "flex-start", textTransform: "none",
  borderColor: "#333", color: "#777",
  "&:hover": { borderColor: TEAL, color: TEAL },
};

const negateLabel = (isNeg) => (
  <Typography sx={{ fontSize: "0.75rem", color: isNeg ? "#c0392b" : "#666", userSelect: "none" }}>
    Exclude (negate)
  </Typography>
);

// ── Shared sub-components ─────────────────────────────────────────────────────

// Card wrapping the active mode content
const ContentCard = ({ children }) => (
  <Box sx={{ backgroundColor: "#0d0d0d", borderRadius: 1.5, border: "1px solid #272727", p: 1.75 }}>
    {children}
  </Box>
);

// Highlighted box for the add-item area
const AddBox = ({ children }) => (
  <Box sx={{ backgroundColor: "rgba(69,144,149,0.03)", border: "1px solid rgba(69,144,149,0.15)", borderRadius: 1.5, p: 1.5, mt: 0.75 }}>
    {children}
  </Box>
);

// List item row with left teal strip
const ItemRow = ({ label, onRemove }) => (
  <Stack direction="row" alignItems="center" spacing={0.75}
    sx={{ borderRadius: 1, border: "1px solid #222", borderLeft: `3px solid rgba(69,144,149,0.5)`, pl: 1.1, pr: 0.75, py: 0.5, backgroundColor: "#0a0a0a" }}>
    <Typography component="span" sx={{ flex: 1, fontFamily: "monospace", fontSize: "0.82rem", color: "#ccc" }}>
      {label}
    </Typography>
    <IconButton size="small" onClick={onRemove} sx={{ p: 0.25, color: "#444", "&:hover": { color: "#c0392b" } }}>
      <DeleteIcon sx={{ fontSize: 14 }} />
    </IconButton>
  </Stack>
);

// Type selector bar — displayed as a card
const TypeBar = ({ labels, value, onChange }) => (
  <Box sx={{ backgroundColor: "rgba(255,255,255,0.025)", borderRadius: 1.5, border: "1px solid #232323", p: 1.25, mb: 2 }}>
    <Stack direction="row" spacing={0.75} flexWrap="wrap">
      {Object.entries(labels).map(([t, lbl]) => (
        <Button key={t} size="small" onClick={() => onChange(t)} sx={pillSx(value === t)}>{lbl}</Button>
      ))}
    </Stack>
  </Box>
);

const DlgActions = ({ onClose, onConfirm, confirmDisabled }) => (
  <DialogActions sx={{ backgroundColor: "#0f0f0f", borderTop: "1px solid #252525", px: 2.5, py: 1.5, gap: 1 }}>
    <Button variant="outlined" size="small" onClick={onClose}
      sx={{ color: "#888", borderColor: "rgba(255,255,255,0.12)", textTransform: "none", "&:hover": { borderColor: "#666", color: "#bbb" } }}>
      Cancel
    </Button>
    <Button variant="contained" size="small" onClick={onConfirm} disabled={confirmDisabled}
      sx={{ textTransform: "none", backgroundColor: TEAL, "&:hover": { backgroundColor: "#367a7e" }, "&.Mui-disabled": { backgroundColor: "#1e1e1e", color: "#444" } }}>
      Confirm
    </Button>
  </DialogActions>
);

// Styled dialog title with left accent bar + gradient
const DlgTitle = ({ children }) => (
  <DialogTitle sx={{
    background: "linear-gradient(90deg, rgba(69,144,149,0.09) 0%, rgba(69,144,149,0.02) 55%, transparent 100%)",
    borderBottom: "1px solid rgba(69,144,149,0.14)",
    py: 1.75, px: 2.5,
    display: "flex", alignItems: "center", gap: 1.25,
    fontSize: 15, fontWeight: 700, letterSpacing: "0.005em",
  }}>
    <Box sx={{ width: 3, height: 17, backgroundColor: TEAL, borderRadius: 2, flexShrink: 0 }} />
    {children}
  </DialogTitle>
);

// Negate toggle row
const NegateRow = ({ checked, onChange }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
    <Switch size="small" checked={checked} onChange={e => onChange(e.target.checked)} sx={SWITCH_SX} />
    {negateLabel(checked)}
  </Stack>
);

// ── IpBuilderDialog (named export) ───────────────────────────────────────────

export const IpBuilderDialog = ({ open, onClose, initialValue, onConfirm, forDns = false }) => {
  const [type,         setType]         = useState("any");
  const [singleVal,    setSingleVal]    = useState("");
  const [singleNeg,    setSingleNeg]    = useState(false);
  const [singleErr,    setSingleErr]    = useState(false);
  const [listItems,    setListItems]    = useState([]);
  const [listInput,    setListInput]    = useState("");
  const [listInputNeg, setListInputNeg] = useState(false);
  const [listInputErr, setListInputErr] = useState("");
  const [preset,       setPreset]       = useState("home_net");

  useEffect(() => {
    if (!open) return;
    const v = initialValue;
    setSingleErr(false); setListInputErr(""); setListInput(""); setListInputNeg(false);
    if (!v || v.type === "any") { setType("any"); setSingleVal(""); setSingleNeg(false); setListItems([]); setPreset("home_net"); return; }
    setType(v.type);
    if (v.type === "single") { setSingleVal(v.value ?? ""); setSingleNeg(v.negate ?? false); }
    if (v.type === "list")   { setListItems(v.items ?? []); }
    if (v.type === "preset") setPreset(v.value ?? "home_net");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  function changeType(t) { setType(t); setSingleErr(false); setListInputErr(""); }

  function addListItem() {
    const val = listInput.trim();
    if (!val) return;
    if (!isValidIpEntry(val, forDns))        { setListInputErr("Invalid value"); return; }
    if (listItems.some(i => i.value === val)) { setListInputErr("Already in list"); return; }
    setListInputErr("");
    setListItems(prev => [...prev, { value: val, negate: listInputNeg }]);
    setListInput(""); setListInputNeg(false);
  }

  function handleConfirm() {
    if (type === "single" && !isValidIpEntry(singleVal.trim(), forDns)) { setSingleErr(true); return; }
    if (type === "list" && listItems.length === 0) return;
    let result;
    if (type === "any")    result = { type: "any" };
    if (type === "single") result = { type: "single", value: singleVal.trim(), negate: singleNeg };
    if (type === "list")   result = { type: "list", items: listItems };
    if (type === "preset") result = { type: "preset", value: preset, label: IP_PRESET_MAP[preset]?.label };
    onConfirm(result);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={dialogPaperSx}>
      <DlgTitle>Select address</DlgTitle>
      <DialogContent sx={{ pt: 2.5, pb: 1, px: 2.5, marginTop: 2 }}>
        <TypeBar labels={IP_TYPE_LABELS} value={type} onChange={changeType} />

        {type === "any" && (
          <ContentCard>
            <Typography sx={{ fontSize: "0.82rem", color: "#666", fontStyle: "italic" }}>
              Matches any IP address.
            </Typography>
          </ContentCard>
        )}

        {type === "single" && (
          <ContentCard>
            <Stack spacing={1.25}>
              <TextField size="small" fullWidth autoFocus
                label={forDns ? "IP, CIDR or domain" : "IP or CIDR"}
                placeholder={forDns ? "e.g. 192.168.1.1 or evil.com" : "e.g. 192.168.1.0/24"}
                value={singleVal}
                onChange={e => { setSingleVal(e.target.value); setSingleErr(false); }}
                onKeyDown={e => { if (e.key === "Enter") handleConfirm(); }}
                error={singleErr}
                helperText={singleErr ? (forDns ? "Invalid IP, CIDR or domain" : "Invalid IP or CIDR") : " "}
              />
              <NegateRow checked={singleNeg} onChange={setSingleNeg} />
            </Stack>
          </ContentCard>
        )}

        {type === "list" && (
          <Stack spacing={0.75}>
            {listItems.length === 0 && (
              <Typography sx={{ fontSize: "0.78rem", color: "#555", fontStyle: "italic", py: 0.5, px: 0.25 }}>No addresses added yet.</Typography>
            )}
            {listItems.map((item, i) => (
              <ItemRow key={i}
                label={<span style={{ color: item.negate ? "#c0392b" : "#ccc" }}>{item.negate ? "! " : ""}{item.value}</span>}
                onRemove={() => setListItems(prev => prev.filter((_, j) => j !== i))}
              />
            ))}
            <AddBox>
              <Stack direction="row" spacing={0.75} alignItems="flex-start">
                <TextField size="small"
                  placeholder={forDns ? "IP, CIDR or domain" : "IP or CIDR"}
                  value={listInput}
                  autoFocus={listItems.length === 0}
                  onChange={e => { setListInput(e.target.value); setListInputErr(""); }}
                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addListItem(); } }}
                  error={!!listInputErr} helperText={listInputErr || " "}
                  sx={{ flex: 1 }}
                />
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ pt: 0.9, flexShrink: 0 }}>
                  <Switch size="small" checked={listInputNeg} onChange={e => setListInputNeg(e.target.checked)} sx={SWITCH_SX} />
                  <Typography sx={{ fontSize: "0.7rem", color: listInputNeg ? "#c0392b" : "#666", whiteSpace: "nowrap" }}>Negate</Typography>
                </Stack>
                <Button size="small" variant="outlined" onClick={addListItem} startIcon={<AddIcon />}
                  sx={{ ...addBtnSx, mt: 0.25, flexShrink: 0 }}>Add</Button>
              </Stack>
            </AddBox>
          </Stack>
        )}

        {type === "preset" && (
          <Stack spacing={0.75}>
            {IP_PRESETS.map(p => (
              <Box key={p.value} onClick={() => setPreset(p.value)}
                sx={{ cursor: "pointer", p: 1.5, borderRadius: 1.5, border: `1px solid ${preset === p.value ? TEAL : "#222"}`, backgroundColor: preset === p.value ? "rgba(69,144,149,0.09)" : "#0c0c0c", transition: "all 0.12s", "&:hover": { borderColor: "rgba(69,144,149,0.6)", backgroundColor: "rgba(69,144,149,0.05)" } }}>
                <Typography sx={{ fontWeight: 600, color: preset === p.value ? "#e8e8e8" : "#aaa", fontSize: "0.85rem" }}>{p.label}</Typography>
                <Typography sx={{ fontSize: "0.72rem", color: "#555", mt: 0.25 }}>{p.desc}</Typography>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DlgActions onClose={onClose} onConfirm={handleConfirm} confirmDisabled={type === "list" && listItems.length === 0} />
    </Dialog>
  );
};

// ── PortBuilderDialog (named export) ─────────────────────────────────────────

export const PortBuilderDialog = ({ open, onClose, initialValue, onConfirm }) => {
  const [type,      setType]      = useState("any");
  const [singleVal, setSingleVal] = useState("");
  const [singleNeg, setSingleNeg] = useState(false);
  const [singleErr, setSingleErr] = useState("");
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo,   setRangeTo]   = useState("");
  const [rangeNeg,  setRangeNeg]  = useState(false);
  const [rangeErr,  setRangeErr]  = useState("");
  const [listItems, setListItems] = useState([]);
  // List add-item sub-form
  const [addKind,     setAddKind]     = useState("single");
  const [addVal,      setAddVal]      = useState("");
  const [addNeg,      setAddNeg]      = useState(false);
  const [addValErr,   setAddValErr]   = useState("");
  const [addFrom,     setAddFrom]     = useState("");
  const [addTo,       setAddTo]       = useState("");
  const [addRangeNeg, setAddRangeNeg] = useState(false);
  const [addRangeErr, setAddRangeErr] = useState("");

  function resetAddForm() {
    setAddVal(""); setAddNeg(false); setAddValErr("");
    setAddFrom(""); setAddTo(""); setAddRangeNeg(false); setAddRangeErr("");
  }

  useEffect(() => {
    if (!open) return;
    const v = initialValue;
    setSingleErr(""); setRangeErr(""); resetAddForm(); setAddKind("single");
    if (!v || v.type === "any") { setType("any"); setSingleVal(""); setSingleNeg(false); setRangeFrom(""); setRangeTo(""); setRangeNeg(false); setListItems([]); return; }
    setType(v.type);
    if (v.type === "single") { setSingleVal(v.value ?? ""); setSingleNeg(v.negate ?? false); }
    if (v.type === "range")  { setRangeFrom(v.from ?? ""); setRangeTo(v.to ?? ""); setRangeNeg(v.negate ?? false); }
    if (v.type === "list")   { setListItems(v.items ?? []); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  function changeType(t) { setType(t); setSingleErr(""); setRangeErr(""); resetAddForm(); }
  function changeAddKind(k) { setAddKind(k); setAddValErr(""); setAddRangeErr(""); }

  function isDupSingle(val) { return listItems.some(i => i.type === "single" && i.value === val); }
  function isDupRange(from, to) { return listItems.some(i => i.type === "range" && i.from === from && i.to === to); }

  function addPortItem() {
    if (addKind === "single") {
      if (!isValidPortNum(addVal)) { setAddValErr("Invalid port (1–65535)"); return; }
      if (isDupSingle(addVal))     { setAddValErr("Already in list"); return; }
      setListItems(prev => [...prev, { type: "single", value: addVal, negate: addNeg }]);
      setAddVal(""); setAddNeg(false); setAddValErr("");
    } else {
      if (!isValidPortNum(addFrom) || !isValidPortNum(addTo)) { setAddRangeErr("Invalid range"); return; }
      if (isDupRange(addFrom, addTo))                          { setAddRangeErr("Already in list"); return; }
      setListItems(prev => [...prev, { type: "range", from: addFrom, to: addTo, negate: addRangeNeg }]);
      setAddFrom(""); setAddTo(""); setAddRangeNeg(false); setAddRangeErr("");
    }
  }

  function handleConfirm() {
    if (type === "single" && !isValidPortNum(singleVal)) { setSingleErr("Invalid port (1–65535)"); return; }
    if (type === "range" && (!isValidPortNum(rangeFrom) || !isValidPortNum(rangeTo))) { setRangeErr("Invalid range"); return; }
    if (type === "list" && listItems.length === 0) return;
    let result;
    if (type === "any")    result = { type: "any" };
    if (type === "single") result = { type: "single", value: singleVal, negate: singleNeg };
    if (type === "range")  result = { type: "range",  from: rangeFrom, to: rangeTo, negate: rangeNeg };
    if (type === "list")   result = { type: "list",   items: listItems };
    onConfirm(result);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={dialogPaperSx}>
      <DlgTitle>Select port</DlgTitle>
      <DialogContent sx={{ pt: 2.5, pb: 1, px: 2.5 }}>
        <TypeBar labels={PORT_TYPE_LABELS} value={type} onChange={changeType} />

        {type === "any" && (
          <ContentCard>
            <Typography sx={{ fontSize: "0.82rem", color: "#666", fontStyle: "italic" }}>
              Matches any port.
            </Typography>
          </ContentCard>
        )}

        {type === "single" && (
          <ContentCard>
            <Stack spacing={1.25}>
              <TextField size="small" fullWidth autoFocus label="Port number" placeholder="e.g. 443"
                value={singleVal}
                onChange={e => { setSingleVal(e.target.value); setSingleErr(""); }}
                onKeyDown={e => { if (e.key === "Enter") handleConfirm(); }}
                error={!!singleErr} helperText={singleErr || " "}
              />
              <NegateRow checked={singleNeg} onChange={setSingleNeg} />
            </Stack>
          </ContentCard>
        )}

        {type === "range" && (
          <ContentCard>
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField size="small" label="From" placeholder="e.g. 80" value={rangeFrom} autoFocus
                  onChange={e => { setRangeFrom(e.target.value); setRangeErr(""); }}
                  error={!!rangeErr && !isValidPortNum(rangeFrom)}
                  helperText={!!rangeErr && !isValidPortNum(rangeFrom) ? "Invalid" : " "}
                  sx={{ flex: 1 }}
                />
                <Typography sx={{ pt: 1.15, color: "#444", fontWeight: 700 }}>:</Typography>
                <TextField size="small" label="To" placeholder="e.g. 443" value={rangeTo}
                  onChange={e => { setRangeTo(e.target.value); setRangeErr(""); }}
                  error={!!rangeErr && !isValidPortNum(rangeTo)}
                  helperText={!!rangeErr && !isValidPortNum(rangeTo) ? "Invalid" : " "}
                  sx={{ flex: 1 }}
                />
              </Stack>
              {!!rangeErr && isValidPortNum(rangeFrom) && isValidPortNum(rangeTo) && (
                <Typography sx={{ fontSize: "0.72rem", color: "#c0392b", mt: -0.75 }}>{rangeErr}</Typography>
              )}
              <NegateRow checked={rangeNeg} onChange={setRangeNeg} />
            </Stack>
          </ContentCard>
        )}

        {type === "list" && (
          <Stack spacing={0.75}>
            {listItems.length === 0 && (
              <Typography sx={{ fontSize: "0.78rem", color: "#555", fontStyle: "italic", py: 0.5, px: 0.25 }}>No ports added yet.</Typography>
            )}
            {listItems.map((item, i) => (
              <ItemRow key={i}
                label={
                  item.type === "range"
                    ? <span style={{ color: item.negate ? "#c0392b" : "#ccc" }}>{item.negate ? "! " : ""}{item.from}<span style={{ color: "#555", margin: "0 3px" }}>:</span>{item.to}</span>
                    : <span style={{ color: item.negate ? "#c0392b" : "#ccc" }}>{item.negate ? "! " : ""}{item.value}</span>
                }
                onRemove={() => setListItems(prev => prev.filter((_, j) => j !== i))}
              />
            ))}

            <AddBox>
              {/* Sub-type selector */}
              <Stack direction="row" spacing={0.5} mb={1.5}>
                <Button size="small" onClick={() => changeAddKind("single")} sx={subPillSx(addKind === "single")}>Single</Button>
                <Button size="small" onClick={() => changeAddKind("range")}  sx={subPillSx(addKind === "range")}>Range</Button>
              </Stack>

              {addKind === "single" && (
                <Stack spacing={1}>
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <TextField size="small" label="Port" placeholder="e.g. 80" autoFocus
                      value={addVal}
                      onChange={e => { setAddVal(e.target.value); setAddValErr(""); }}
                      onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addPortItem(); } }}
                      error={!!addValErr} helperText={addValErr || " "}
                      sx={{ width: 100 }}
                    />
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ pt: 0.9, flexShrink: 0 }}>
                      <Switch size="small" checked={addNeg} onChange={e => setAddNeg(e.target.checked)} sx={SWITCH_SX} />
                      <Typography sx={{ fontSize: "0.7rem", color: addNeg ? "#c0392b" : "#666", whiteSpace: "nowrap" }}>Negate</Typography>
                    </Stack>
                    <Button size="small" variant="outlined" onClick={addPortItem} startIcon={<AddIcon />}
                      sx={{ ...addBtnSx, flexShrink: 0, mt: 0.25 }}>Add</Button>
                  </Stack>
                </Stack>
              )}

              {addKind === "range" && (
                <Stack spacing={1}>
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <TextField size="small" label="From" placeholder="80" autoFocus
                      value={addFrom}
                      onChange={e => { setAddFrom(e.target.value); setAddRangeErr(""); }}
                      onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addPortItem(); } }}
                      error={!!addRangeErr && !isValidPortNum(addFrom)}
                      helperText={!!addRangeErr && !isValidPortNum(addFrom) ? "Invalid" : " "}
                      sx={{ width: 80 }}
                    />
                    <Typography sx={{ pt: 1.15, color: "#444", fontWeight: 700 }}>:</Typography>
                    <TextField size="small" label="To" placeholder="443"
                      value={addTo}
                      onChange={e => { setAddTo(e.target.value); setAddRangeErr(""); }}
                      error={!!addRangeErr && !isValidPortNum(addTo)}
                      helperText={!!addRangeErr && !isValidPortNum(addTo) ? "Invalid" : " "}
                      sx={{ width: 80 }}
                    />
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ pt: 0.9, flexShrink: 0 }}>
                      <Switch size="small" checked={addRangeNeg} onChange={e => setAddRangeNeg(e.target.checked)} sx={SWITCH_SX} />
                      <Typography sx={{ fontSize: "0.7rem", color: addRangeNeg ? "#c0392b" : "#666", whiteSpace: "nowrap" }}>Negate</Typography>
                    </Stack>
                    <Button size="small" variant="outlined" onClick={addPortItem} startIcon={<AddIcon />}
                      sx={{ ...addBtnSx, flexShrink: 0, mt: 0.25 }}>Add</Button>
                  </Stack>
                  {!!addRangeErr && isValidPortNum(addFrom) && isValidPortNum(addTo) && (
                    <Typography sx={{ fontSize: "0.7rem", color: "#c0392b", mt: -0.5 }}>{addRangeErr}</Typography>
                  )}
                </Stack>
              )}
            </AddBox>
          </Stack>
        )}
      </DialogContent>
      <DlgActions onClose={onClose} onConfirm={handleConfirm} confirmDisabled={type === "list" && listItems.length === 0} />
    </Dialog>
  );
};

// ── IpPicker (default export) ─────────────────────────────────────────────────

const IpPicker = ({ value, onChange, placeholder = "Select address", error = false, helperText, forDns = false }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ flex: 1 }}>
      <Box onClick={() => setOpen(true)}
        sx={{ height: 40, border: `1px solid ${error ? "#d32f2f" : "rgba(255,255,255,0.23)"}`, borderRadius: 1, px: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", transition: "border-color 0.15s", "&:hover": { borderColor: error ? "#d32f2f" : "rgba(255,255,255,0.87)" } }}>
        <Typography sx={{ fontSize: "0.875rem", color: value ? "#e0e0e0" : "#555", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {value ? displayIpUi(value) : placeholder}
        </Typography>
        <EditIcon sx={{ fontSize: 14, color: "#555", flexShrink: 0, ml: 1 }} />
      </Box>
      {helperText && <Typography sx={{ fontSize: "0.75rem", color: error ? "#d32f2f" : "rgba(255,255,255,0.6)", mt: 0.375, mx: 1.75 }}>{helperText}</Typography>}
      <IpBuilderDialog open={open} onClose={() => setOpen(false)} initialValue={value} onConfirm={v => { onChange(v); setOpen(false); }} forDns={forDns} />
    </Box>
  );
};

export default IpPicker;
